import { NGXLogger } from 'ngx-logger';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { Page, TallyDetail, TallyDetailId, TallyRow, TallySettings } from '../../components/tallysheet-list/tallysheet-list-round.component';
import { AppConstants } from 'src/app/commons/app-constants';
import { offlineDb } from 'src/app/offline/offline-db';
import { OutboxService, isProvisionalId } from 'src/app/offline/outbox.service';
import { NetworkStatusService } from 'src/app/services/network-status.service';
import { DetailCacheService } from 'src/app/offline/detail-cache.service';
import { isOfflineCapableNative } from 'src/app/commons/utils/platform';
import { rememberKnownKeys, rememberRowBases, stampConflictBases } from 'src/app/offline/tally-row-bases';
import { isPositionClash, placeRows, PositionClashBody, withoutPositionFields } from 'src/app/offline/position-clash';

/** Per-stock-unit Quality-enable switch: response shape shared by the usage-check and
 *  disable-quality endpoints. */
export interface TallyQualityUsage {
  rowsWithQualityCount: number;
  tuQualityCode: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class TallysheetListService {
  private readonly logger = inject(NGXLogger);

  public apiUrl = AppConstants.API_URL + 'api/v1/tally';
  constructor(
    private http: HttpClient,
    private outboxService: OutboxService,
    private networkStatusService: NetworkStatusService,
    private detailCacheService: DetailCacheService,
  ) { }


  /** force = "Keep mine" after a "changed by someone else" refusal: save without the check (a
   *  deliberate overwrite). Every row is stamped with its conflictBase first — see tally-row-bases.ts.
   *  Every editable load and save of tally rows goes through this service, so that is tracked here,
   *  not in the two grids (2026-09-29). */
  saveDetails(details: any, overStockReason?: string, force = false): Observable<any> {
    stampConflictBases(details);
    // Two offline cases, same as saveSettings()/updateGridStyle(): a provisional (negative)
    // tallyMasterId means this master was itself created offline and doesn't exist on the
    // server yet; a real (positive) masterId with the device simply offline right now is
    // equally unable to reach the server. Either way, queue the batch rather than attempting a
    // network call that can only fail. All rows in one saveDetails call share the same master,
    // so checking the first is sufficient.
    const offlineMaster = isProvisionalId(details?.[0]?.id?.tallyMasterId);
    if (isOfflineCapableNative() && details?.length && (offlineMaster || !this.networkStatusService.isOnline)) {
      return this.saveDetailsOffline(details, offlineMaster, overStockReason, force);
    }
    const url = `${this.apiUrl}/detail/batch`;
    this.logger.debug(`Saving details to URL: ${url} with data: ${JSON.stringify(details)}`);
    let params = new HttpParams();
    if (overStockReason) {
      params = params.set('overStockReason', overStockReason);
    }
    if (!force) {
      params = params.set('conflictCheck', 'true');
    }
    // Phone says "online" but the server can't be reached (weak signal, Wi-Fi without internet):
    // queue it like an offline save instead of losing it (OFFLINE_PLAN.md 1.4). Safe to replay —
    // rows are saved by (tally, orderKey), so a save that did reach the server is just saved again.
    return this.http.post<any>(url, details, { params }).pipe(
      tap((res) => rememberRowBases(details[0]?.id?.tallyMasterId, res?.resultObj)),
      catchError((err) => {
        // Someone else added rows at the same place (POSITION_CLASH_PLAN.md): nothing was saved.
        // Place mine after theirs (the default), save again, record the marks. The response then
        // carries `positionPlaced` so the grid can reload and say what happened.
        if (isPositionClash(err)) return this.placeAndSaveAgain(url, details, err.error, params);
        return TallysheetListService.unreachable(err) && details?.length
          ? this.saveDetailsOffline(details, false, overStockReason, force)
          : throwError(() => err);
      }),
    );
  }

  /** See saveDetails(). If the rows can't be placed (no room between two keys), they are saved where
   *  they were — today's behavior — and still marked, so both people can check them. */
  private placeAndSaveAgain(url: string, details: any[], body: PositionClashBody, params: HttpParams): Observable<any> {
    const masterId = details?.[0]?.id?.tallyMasterId;
    const placed = placeRows(details, body, 'B_FIRST');
    const rows = placed ? placed.rows : withoutPositionFields(details);
    const record = placed ? placed.record : {
      masterId,
      choice: 'AS_IS',
      clashes: (body.clashes || []).map((c) => ({
        mine: (c.mineKeys || []).map((k) => ({ key: k, movedFrom: k })),
        theirs: (c.theirs || []).map((t) => t.orderKey),
      })),
    };
    const names = placed ? placed.otherNames : [...new Set((body.clashes || [])
      .flatMap((c) => (c.theirs || []).map((t) => t.createdByName || t.createdBy || '')))].filter(Boolean);
    const count = placed ? placed.placedCount : (body.clashes || []).reduce((n, c) => n + (c.mineKeys?.length || 0), 0);
    this.logger.info('[PositionClash] placing rows and saving again', { masterId, count, asIs: !placed });
    return this.http.post<any>(url, rows, { params }).pipe(
      tap((res) => rememberRowBases(masterId, res?.resultObj)),
      switchMap((res) => this.http.post<any>(`${this.apiUrl}/row-clash/record`, record).pipe(
        catchError(() => of(null)), // the rows are saved; a missing mark is not worth failing the save
        map(() => ({ ...res, positionPlaced: { count, names, asIs: !placed, moved: placed?.moved ?? {} } })),
      )),
    );
  }

  // ── Position clash — the "Clashing rows" bar (POSITION_CLASH_PLAN.md) ─────────────────────────
  /** What the Change dialog needs for one clash: both sides, who may remove, where rows can go. */
  getRowClash(clashId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/row-clash/${encodeURIComponent(clashId)}`);
  }

  /** Applies a choice: A_FIRST / B_FIRST (with newKeys for side A), REMOVE_A, REMOVE_B, KEEP. */
  resolveRowClash(clashId: string, choice: string, newKeys?: Record<string, string>): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/row-clash/${encodeURIComponent(clashId)}/resolve`, { choice, newKeys: newKeys ?? {} });
  }

  /** Native app + the request never reached the server (status 0): the caller may queue it instead. */
  static unreachable(err: any): boolean {
    return isOfflineCapableNative() && err?.status === 0;
  }

  // Merges the batch into pendingRows (keyed by the master's clientUuid) and queues a
  // SAVE_TALLY_DETAILS outbox op. Echoes back a response shaped like the real batch endpoint's
  // {result, resultObj} so the round/square grid's existing success handler (which clears
  // each row's `changed` flag) works unmodified. Same dependency convention as
  // saveSettingsOffline/updateGridStyleOffline: a provisional master resolves its clientUuid
  // from the owning offline-created TU (recovered from the detail cache entry seeded at
  // offline TU-create time); an already-real master (device just happens to be offline) gets
  // its own synthetic per-master clientUuid instead, since there's no owning create to depend
  // on — SyncEngineService.replaySaveTallyDetails() recognizes this by its `tally-` prefix and
  // uses the payload's own tallyMasterId as-is rather than resolving it through idMap.
  private saveDetailsOffline(details: any[], offlineMaster: boolean, overStockReason?: string, force = false): Observable<any> {
    const tallyMasterId = details[0].id.tallyMasterId;
    const clientUuid$ = offlineMaster
      ? from(this.findClientUuidForProvisionalMaster(tallyMasterId))
      : of(`tally-${tallyMasterId}`);
    return clientUuid$.pipe(
      switchMap((clientUuid) => {
        if (!clientUuid) {
          throw new Error(`No offline transport unit found for provisional master ${tallyMasterId}`);
        }
        return from(this.mergePendingRows(clientUuid, details)).pipe(
          // The over-stock reason typed with these rows travels with them (OFFLINE_PLAN.md 1.3) —
          // without it the replay is refused as over stock and the rows are lost.
          // force=1 ("Keep mine" after a row conflict) travels with the queued save too — see SyncEngine.
          switchMap(() => {
            const params: Record<string, string> = {};
            if (overStockReason) params['overStockReason'] = overStockReason;
            if (force) params['force'] = '1';
            return this.outboxService.enqueue('SAVE_TALLY_DETAILS', clientUuid, details,
              Object.keys(params).length ? params : undefined);
          }),
          map(() => ({ result: 'success', resultObj: details })),
        );
      }),
    );
  }

  // No longer private — CustomMadeRequestDialogComponent needs the exact same "resolve this
  // provisional master's owning TU clientUuid" lookup to correctly queue an offline Custom-Made
  // request as dependent on its owning TU's own sync, mirroring how SAVE_TALLY_DETAILS/
  // UPLOAD_DOCUMENT already do this. Same method, same behavior — only the visibility changed.
  // Phase 2 (2026-09-29): was a full read of the whole detail cache (every tally page, every document
  // file) on EVERY call — and the grid calls this on each load/save of an offline-created tally. Now
  // one scan builds an index of every offline-created tally line → its transport unit's clientUuid,
  // reused until a lookup misses (a transport unit created after the last scan) — then it rescans,
  // at most once per few seconds so a genuinely unknown id can't trigger a scan storm.
  // Provisional ids are unique and never reused, so an indexed entry never goes stale.
  private static provisionalOwner = new Map<number, string>();
  private static recentMisses = new Map<number, number>();

  /** Called the moment a transport unit is created/edited offline, so its lines are known without a scan. */
  static rememberProvisionalOwner(detail: any): void {
    const owner = detail?._offlineClientUuid;
    if (!owner) return;
    if (detail.tallyMasterId != null) TallysheetListService.provisionalOwner.set(detail.tallyMasterId, owner);
    (detail.additionalLines || []).forEach((line: any) => {
      if (line?.tallyMasterId != null) TallysheetListService.provisionalOwner.set(line.tallyMasterId, owner);
    });
  }

  async findClientUuidForProvisionalMaster(tallyMasterId: number): Promise<string | undefined> {
    const known = TallysheetListService.provisionalOwner.get(tallyMasterId);
    if (known) return known;
    // The same unknown id asked again within 3s → same answer, no new scan (a different id always scans).
    const missedAt = TallysheetListService.recentMisses.get(tallyMasterId);
    if (missedAt && Date.now() - missedAt < 3_000) return undefined;
    const offlineCreated = await offlineDb.detailCache
      .filter((entry) => !!(entry.data as any)?._offlineClientUuid)
      .toArray();
    offlineCreated.forEach((entry) => TallysheetListService.rememberProvisionalOwner(entry.data));
    const found = TallysheetListService.provisionalOwner.get(tallyMasterId);
    if (!found) TallysheetListService.recentMisses.set(tallyMasterId, Date.now());
    return found;
  }

  private async mergePendingRows(clientUuid: string, details: any[]): Promise<void> {
    const existing = await offlineDb.pendingRows.get(clientUuid);
    const rows = existing?.rows ? [...existing.rows] : [];
    details.forEach((detail) => {
      const idx = rows.findIndex((r: any) => r.id?.orderKey === detail.id?.orderKey);
      if (idx >= 0) {
        rows[idx] = detail;
      } else {
        rows.push(detail);
      }
    });
    await offlineDb.pendingRows.put({ clientUuid, rows });
  }

  fetchDetails(masterId: number, page: number, size: number): Observable<TallyDetail[]> {
    if (isOfflineCapableNative() && isProvisionalId(masterId)) {
      return this.fetchDetailsOffline(masterId);
    }
    const url = `${this.apiUrl}/details/${masterId}?page=${page}&size=${size}`;
    this.logger.info(`Fetching details from URL: ${url}`);
    const request$ = this.detailCacheService.cacheThrough(`tallyDetails:${masterId}:${page}:${size}`, () => this.http.get<TallyDetail[]>(url))
      // Server rows only (before offline-queued rows are merged in below) — see rowBase.
      .pipe(tap((rows) => rememberRowBases(masterId, rows as any[], true)));
    // Merge in any rows saved offline against this already-real master but not yet synced —
    // queued under the same synthetic `tally-<masterId>` clientUuid saveDetailsOffline uses.
    // Without this, a row saved offline for an already-synced TU is correctly queued for sync
    // (see saveDetailsOffline) but never actually shown in the grid until it syncs — this fetch
    // path had no awareness of pendingRows at all otherwise (only fetchDetailsOffline's
    // provisional-master path did). Confirmed live 2026-08-05: Save reported success, but the
    // row was silently missing on reload. Only merged into page 0 — pendingRows isn't
    // page-aware (a provisional master's own equivalent has zero real pagination to worry
    // about), so merging it into every page would duplicate the same rows across all of them.
    if (!isOfflineCapableNative() || page !== 0) return request$;
    return request$.pipe(
      switchMap((rows) => from(offlineDb.pendingRows.get(`tally-${masterId}`)).pipe(
        map((entry) => this.mergePendingRowsIntoResults(rows, (entry?.rows as TallyDetail[]) ?? [])),
      )),
    );
  }

  private mergePendingRowsIntoResults(rows: TallyDetail[], pending: TallyDetail[]): TallyDetail[] {
    if (!pending.length) return rows;
    const merged = [...rows];
    pending.forEach((row) => {
      const idx = merged.findIndex((r: any) => r.id?.orderKey === (row as any).id?.orderKey);
      if (idx >= 0) merged[idx] = row;
      else merged.push(row);
    });
    return merged;
  }

  // A provisional master has no server-side rows at all — never attempt the network, just
  // return whatever's accumulated in pendingRows for it (empty on first load).
  private fetchDetailsOffline(masterId: number): Observable<TallyDetail[]> {
    return from(this.findClientUuidForProvisionalMaster(masterId)).pipe(
      switchMap((clientUuid) => clientUuid
        ? from(offlineDb.pendingRows.get(clientUuid))
        : of(undefined)),
      map((entry) => (entry?.rows as TallyDetail[]) ?? []),
    );
  }

  /*fetchDetails(masterId: number, page: number, size: number): Observable<Page<TallyDetail>> {
    return this.http.get<Page<TallyDetail>>(`/api/tally/details/page/${masterId}?page=${page}&size=${size}`);
  }*/

  deleteDetails(ids: TallyDetailId[]): Observable<void> {
    const offlineMaster = isProvisionalId(ids?.[0]?.tallyMasterId);
    if (isOfflineCapableNative() && ids?.length && (offlineMaster || !this.networkStatusService.isOnline)) {
      return this.deleteDetailsOffline(ids, offlineMaster);
    }
    const url = `${this.apiUrl}/detail/delete`;
    this.logger.debug(`Deleting details with IDs: ${JSON.stringify(ids)} from URL: ${url}`);
    return this.http.post<void>(url, ids).pipe(
      catchError((err) => TallysheetListService.unreachable(err) && ids?.length
        ? this.deleteDetailsOffline(ids, false)
        : throwError(() => err)),
    );
  }

  deleteSquareDetails(ids: TallyDetailId[]): Observable<void> {
    const offlineMaster = isProvisionalId(ids?.[0]?.tallyMasterId);
    if (isOfflineCapableNative() && ids?.length && (offlineMaster || !this.networkStatusService.isOnline)) {
      return this.deleteDetailsOffline(ids, offlineMaster);
    }
    const url = `${this.apiUrl}/detail/delete`;
    this.logger.debug(`Deleting details with IDs: ${JSON.stringify(ids)} from URL: ${url}`);
    return this.http.post<void>(url, ids).pipe(
      catchError((err) => TallysheetListService.unreachable(err) && ids?.length
        ? this.deleteDetailsOffline(ids, false)
        : throwError(() => err)),
    );
  }

  // Two offline cases, same split as saveDetailsOffline: rows for a still-provisional master
  // only ever existed in pendingRows (never reached the server) — just remove them locally,
  // nothing to queue. Rows for an already-real master (device just happens to be offline) DO
  // exist on the server, so they need a genuine DELETE_TALLY_DETAILS replay once back online —
  // same synthetic `tally-<masterId>` clientUuid as saveDetailsOffline, so a row saved offline
  // then deleted offline before either syncs still replays in the same order it happened
  // (outbox is seq-ordered), ending up correctly deleted rather than resurrected. Also clears
  // any matching local pendingRows in the real-master case so the row disappears from the grid
  // immediately instead of only after the next sync (harmless no-op if none exist there).
  private deleteDetailsOffline(ids: TallyDetailId[], offlineMaster: boolean): Observable<void> {
    const tallyMasterId = ids[0].tallyMasterId;
    if (offlineMaster) {
      return from(this.findClientUuidForProvisionalMaster(tallyMasterId)).pipe(
        switchMap((clientUuid) => clientUuid ? from(this.removePendingRows(clientUuid, ids)) : of(undefined)),
        map(() => undefined),
      );
    }
    const clientUuid = `tally-${tallyMasterId}`;
    return from(this.removePendingRows(clientUuid, ids)).pipe(
      switchMap(() => this.outboxService.enqueue('DELETE_TALLY_DETAILS', clientUuid, ids)),
      map(() => undefined),
    );
  }

  private async removePendingRows(clientUuid: string, ids: TallyDetailId[]): Promise<void> {
    const existing = await offlineDb.pendingRows.get(clientUuid);
    if (!existing) return;
    const orderKeysToRemove = new Set(ids.map((id) => id.orderKey));
    const rows = existing.rows.filter((r: any) => !orderKeysToRemove.has(r.id?.orderKey));
    await offlineDb.pendingRows.put({ clientUuid, rows });
  }

  // Gate for the CSV/Excel import dialog — checked before opening it, since the dialog itself
  // makes no backend call (file is parsed client-side) and its rows are later saved through the
  // same endpoint manual row entry uses. A 403 here (PLAN_FEATURE_DISABLED) is already turned
  // into a plan-limit toast by the global HTTP interceptor.
  checkImportEligibility(): Observable<void> {
    return this.http.get<void>(`${this.apiUrl}/import/eligibility`);
  }

  // New method to fetch existing orderKeys
  fetchOrderKeys(masterId: number): Observable<string[]> {
    if (isProvisionalId(masterId)) return of([]);
    return this.http.get<string[]>(`${this.apiUrl}/details/${masterId}/order-keys`)
      // Every key on the server = what this device "knows" when it adds a row (position clash, 2026-09-29).
      .pipe(tap((keys) => rememberKnownKeys(masterId, keys)));
  }

  /** Fixed (not per-master) detailCache key — write-through target for "whatever real tally
   *  settings this device most recently saw from any master." Deliberately shared across every
   *  masterId rather than keyed, since its only consumer is fetchLastKnownSettingsFallback()
   *  below, which has no real masterId to key by in the first place. */
  private static readonly LAST_KNOWN_SETTINGS_KEY = 'lastKnownTallySettings';

  fetchSettings(masterId: number): Observable<TallySettings> {
    // No settings endpoint exists yet for a master that isn't on the server — a freshly
    // created offline TU has no settings of its own yet, falls back to the last real ones seen.
    if (!masterId || isProvisionalId(masterId)) return this.fetchLastKnownSettingsFallback();

    const url = `${this.apiUrl}/settings/${masterId}`;
    this.logger.info(`Fetching settings from URL: ${url} for masterId: ${masterId}`);
    return this.detailCacheService.cacheThrough(`tallySettings:${masterId}`, () => this.http.get<TallySettings>(url)).pipe(
      tap((settings) => {
        // Best-effort write-through, deliberately not gated on isOnline/isOfflineCapableNative —
        // a cache hit replaying through cacheThrough above should refresh this too, and the
        // Dexie write itself is already only reached on native (offlineDb is a no-op shim on web).
        if (isOfflineCapableNative()) {
          offlineDb.detailCache.put({ key: TallysheetListService.LAST_KNOWN_SETTINGS_KEY, data: settings, fetchedAt: Date.now() });
        }
      }),
    );
  }

  // A provisional (or missing) master permanently returned a bare `{}` before this fix, which
  // tallysheet-list-round.component.ts's own hardcoded 5cm/2cm/CM/3-decimal literals then
  // silently filled in — every offline-created TU used those defaults forever, even for a
  // tenant that had long since configured different real settings, since a provisional master's
  // settings response is never refetched once it syncs unless the user happens to reopen the
  // form. Falling back to the last real settings this device has actually seen (any master, same
  // tenant — detailCache has no cross-tenant leak risk, it's wiped on tenant switch same as every
  // other offline cache) is a meaningfully better guess, without needing a new backend endpoint.
  private fetchLastKnownSettingsFallback(): Observable<TallySettings> {
    if (!isOfflineCapableNative()) return of({} as TallySettings);
    return from(offlineDb.detailCache.get(TallysheetListService.LAST_KNOWN_SETTINGS_KEY)).pipe(
      map((entry) => (entry?.data as TallySettings) ?? ({} as TallySettings)),
    );
  }

  saveSettings(settings: any): Observable<TallySettings> {
    const offlineMaster = isProvisionalId(settings?.masterId);
    if (isOfflineCapableNative() && (offlineMaster || !this.networkStatusService.isOnline)) {
      return this.saveSettingsOffline(settings, offlineMaster);
    }
    const url = `${this.apiUrl}/settings`;
    this.logger.debug(`Saving settings to URL: ${url} with data: ${JSON.stringify(settings)}`);

    return this.http.post<TallySettings>(url, settings).pipe(
      catchError((err) => TallysheetListService.unreachable(err)
        ? this.saveSettingsOffline(settings, false)
        : throwError(() => err)),
    );
  }

  /** Per-stock-unit Quality-enable switch: authoritative count of rows still carrying a grade —
   *  called fresh right before showing/skipping the destructive-action confirmation dialog, never
   *  cached, since an in-grid edit not yet saved could otherwise be undercounted. */
  getQualityUsage(masterId: number): Observable<TallyQualityUsage> {
    return this.http.get<TallyQualityUsage>(`${this.apiUrl}/settings/${masterId}/quality/usage`);
  }

  /** Per-stock-unit Quality-enable switch: the destructive OFF transition — turns the switch off,
   *  clears the TU-level grade, and bulk-clears every row's grade for this tally. Online-only;
   *  the user must be connected to perform this. */
  disableQuality(masterId: number): Observable<TallyQualityUsage> {
    return this.http.post<TallyQualityUsage>(`${this.apiUrl}/settings/${masterId}/quality/disable`, {});
  }

  // Queues the settings change for replay once back online (see SyncEngineService). If the
  // master itself is still provisional (its owning TU was created offline and hasn't synced
  // yet), the op is linked to that TU's clientUuid so the real masterId can be resolved from
  // idMap once it syncs — same dependency pattern as SAVE_TALLY_DETAILS. If the master is
  // already real and only the device is offline, there's no owning create to depend on, so a
  // synthetic per-master clientUuid is used instead (a standalone leaf op).
  private saveSettingsOffline(settings: any, offlineMaster: boolean): Observable<TallySettings> {
    const masterId = settings.masterId;
    const clientUuid$ = offlineMaster
      ? from(this.findClientUuidForProvisionalMaster(masterId))
      : of(`settings-${masterId}`);
    return clientUuid$.pipe(
      switchMap((clientUuid) => {
        if (!clientUuid) {
          this.logger.warn('No offline transport unit found for provisional master — settings change dropped', masterId);
          return of(settings as TallySettings);
        }
        return from(this.outboxService.enqueue('SAVE_TALLY_SETTINGS', clientUuid, settings)).pipe(
          map(() => settings as TallySettings),
        );
      }),
    );
  }

  // Add
  fetchAllForExport(masterId: number): Observable<any> {
    const url = `${this.apiUrl}/details/${masterId}?page=0&size=9999`;
    this.logger.info(`Fetching all rows for export from URL: ${url}`);
    return this.http.get<any>(url);
  }

  fetchDocuments(masterId: number): Observable<any[]> {
    if (!masterId || isProvisionalId(masterId)) return of([] as any);
    return this.detailCacheService.cacheThrough(`tallyDocuments:${masterId}`, () => this.http.get<any[]>(`${this.apiUrl}/documents/${masterId}`));
  }

  deleteDocument(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/documents/${id}`);
  }

  updateDocument(id: number, formData: FormData): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/documents/${id}`, formData);
  }

  updateDocumentMetadata(id: number, formData: FormData): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/documents/${id}/metadata`, formData);
  }

  // Queues a photo upload for replay once back online (see SyncEngineService.replayUploadDocument).
  // Same dependency convention as saveSettingsOffline: a provisional masterId means the owning
  // TU was itself created offline and hasn't synced yet, so the op is linked to that TU's
  // clientUuid (idMap resolves the real masterId once CREATE_TU syncs). An already-real masterId
  // (device just happens to be offline) gets its own unique clientUuid per file — unlike
  // settings, multiple photos can queue against the same master, so a per-master synthetic id
  // would incorrectly correlate independent uploads should one of them get rejected.
  async queueDocumentUpload(masterId: number, file: File, category: string, privacyType: string, note: string): Promise<void> {
    const clientUuid = isProvisionalId(masterId)
      ? await this.findClientUuidForProvisionalMaster(masterId)
      : `doc-${masterId}-${uuidv4()}`;
    if (!clientUuid) {
      throw new Error(`No offline transport unit found for provisional master ${masterId}`);
    }
    await this.outboxService.enqueue('UPLOAD_DOCUMENT', clientUuid, { masterId, file, category, privacyType, note });
  }
}
